import React, { useState } from 'react';
import type { TravelDetailsData, PlanSelection } from '../../types/customer-portal';
import { usePlanSelection } from '../../contexts/PlanSelectionContext';
import { useVerifyEmailAddress } from '../../hooks/useVerifyEmailAddress';
import './EmailSentConfirmationSection.css';

interface EmailSentConfirmationSectionProps {
  email?: string;
  travelData?: TravelDetailsData;
  planSelection?: PlanSelection;
  onClose?: () => void;
}

const EmailSentConfirmationSection: React.FC<EmailSentConfirmationSectionProps> = ({
  email = '',
  travelData,
  planSelection,
  onClose
}) => {
  const [isResending, setIsResending] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(true);
  
  // Get selected plan from context
  const { selectedPlan } = usePlanSelection();
  
  // Use the email verification hook for resend functionality
  const { verifyEmail, loading: isValidating, error: apiError, clearState } = useVerifyEmailAddress();

  // Function to mask email address (pi****@g****.com format)
  const maskEmail = (email: string): string => {
    if (!email || !email.includes('@')) return email;
    
    const [localPart, domain] = email.split('@');
    const [domainName, extension] = domain.split('.');
    
    // Mask local part - show first 2 characters, then asterisks
    const maskedLocal = localPart.length > 2 
      ? localPart.substring(0, 2) + '*'.repeat(Math.max(localPart.length - 2, 4))
      : localPart + '****';
    
    // Mask domain - show first character, then asterisks
    const maskedDomain = domainName.length > 1
      ? domainName.substring(0, 1) + '*'.repeat(Math.max(domainName.length - 1, 4))
      : domainName + '****';
    
    return `${maskedLocal}@${maskedDomain}.${extension}`;
  };

  const handleResend = async () => {
    if (!email || !travelData || !planSelection || !selectedPlan) {
      console.error('Missing required data for resend');
      return;
    }

    setIsResending(true);
    clearState();

    try {
      const result = await verifyEmail(
        email,
        travelData,
        planSelection,
        selectedPlan
      );

      if (result.success) {
        // Show success notification briefly
        setShowSuccessNotification(true);
        setTimeout(() => {
          setShowSuccessNotification(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error resending email:', error);
    } finally {
      setIsResending(false);
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleDismissNotification = () => {
    setShowSuccessNotification(false);
  };

  return (
    <div className="email-sent-confirmation-container">
      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="success-notification">
          <div className="success-notification-content">
            <div className="success-icon">✓</div>
            <span className="success-text">Email Verification Sent!</span>
            <button 
              className="notification-close-btn"
              onClick={handleDismissNotification}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="email-sent-content">
        <h1 className="email-sent-title">
          We've sent a verification email<br />
          to {maskEmail(email)}
        </h1>
        
        <p className="email-sent-description">
          You should receive the email within a few minutes. If<br />
          you don't see it, check your spam folder.
        </p>

        <div className="email-sent-actions">
          <p className="resend-text">
            Didn't receive it? <button 
              className="resend-link"
              onClick={handleResend}
              disabled={isResending || isValidating}
            >
              {isResending || isValidating ? 'Sending...' : 'Click here to Resend'}
            </button>.
          </p>
          
          {apiError && (
            <div className="resend-error" role="alert">
              {apiError}
            </div>
          )}
          
          <button
            type="button"
            className="close-button"
            onClick={handleClose}
            disabled={isResending || isValidating}
          >
            Close
          </button>
        </div>

        {/* Contact Information */}
        <div className="contact-section">
          <p className="contact-text">
            Need assistance? Email us at<br />
            <a 
              href="mailto:traveldirect_team@pacificcross.com.ph" 
              className="contact-email"
            >
              traveldirect_team@pacificcross.com.ph
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailSentConfirmationSection;